/**
 * Sprint T-15 · Founder Security Center · 2FA tab.
 *
 * Consumes ONLY /api/auth/2fa/*. Flow:
 *   1. /status (initial)
 *   2. /enable (returns secret + QR)
 *   3. /enable/verify (TOTP) → backup codes (shown once)
 *   4. /verify (sensitive op test)
 *   5. /backup-codes/regenerate (TOTP gate)
 *   6. /disable (TOTP gate · superadmin blocked)
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, ShieldCheck, Smartphone, KeyRound, RotateCw, ShieldOff, Sparkles } from "lucide-react";
import { WidgetCard, WidgetGrid, EmptyState, LoadingState, CitationBadge } from "../advisor";
import {
  useTfaStatus, useTfaEnable, useTfaEnableVerify, useTfaDisable, useTfaRegenerateBackup,
} from "../../hooks/useSecurity";

function CopyButton({ value, label, testId }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      data-testid={testId || "tfa-copy-btn"}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          toast.success(label || "Copiado");
          setTimeout(() => setCopied(false), 2000);
        } catch {
          toast.error("No se pudo copiar");
        }
      }}
      className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)]"
    >
      <Copy className="h-3 w-3" /> {copied ? "Copiado" : "Copiar"}
    </button>
  );
}

export default function TwoFactorTab() {
  const status = useTfaStatus();
  const enable = useTfaEnable();
  const enableVerify = useTfaEnableVerify();
  const disable = useTfaDisable();
  const regenerate = useTfaRegenerateBackup();

  const [enableData, setEnableData] = useState(null);     // { secret, qr_png_base64, provisioning_uri }
  const [backupCodes, setBackupCodes] = useState([]);     // shown ONCE after verify
  const [enableCode, setEnableCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [regenCode, setRegenCode] = useState("");

  const onStartEnable = async () => {
    try {
      const data = await enable.mutateAsync();
      setEnableData(data);
      setBackupCodes([]);
      toast.message("Escanea el QR y verifica con un código TOTP");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error iniciando 2FA");
    }
  };

  const onVerifyEnable = async (e) => {
    e?.preventDefault?.();
    try {
      const res = await enableVerify.mutateAsync(enableCode.trim());
      setBackupCodes(res.backup_codes || []);
      setEnableData(null);
      setEnableCode("");
      toast.success("2FA activado correctamente");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Código inválido");
    }
  };

  const onDisable = async (e) => {
    e?.preventDefault?.();
    try {
      await disable.mutateAsync(disableCode.trim());
      setDisableCode("");
      toast.success("2FA desactivado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Código inválido");
    }
  };

  const onRegenerate = async (e) => {
    e?.preventDefault?.();
    try {
      const res = await regenerate.mutateAsync(regenCode.trim());
      setBackupCodes(res.backup_codes || []);
      setRegenCode("");
      toast.success("Códigos de respaldo regenerados");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Código inválido");
    }
  };

  const s = status.data;
  const enabled = !!s?.enabled;
  const mandatory = !!s?.is_mandatory_for_role;

  return (
    <section className="space-y-4" data-testid="security-2fa-tab">
      <WidgetCard
        title="Estado 2FA"
        description={mandatory ? "Obligatorio para tu rol (superadmin)" : "Opcional · refuerza la seguridad de tu cuenta"}
        citations={["/api/auth/2fa/status"]}
        testId="widget-tfa-status"
      >
        {status.isLoading && <LoadingState />}
        {status.error && <EmptyState message="No se pudo consultar el estado de 2FA." />}
        {s && (
          <div className="flex items-center gap-3" data-testid="tfa-status-block">
            <ShieldCheck className={`h-6 w-6 ${enabled ? "text-emerald-500" : "text-[var(--ag-text-dim)]"}`} />
            <div className="flex-1">
              <div className="text-lg font-semibold">{enabled ? "2FA activo" : "2FA inactivo"}</div>
              <div className="text-[11px] text-[var(--ag-text-dim)]">
                Rol: {s.role} · {mandatory ? "obligatorio" : "opcional"} · {s.backup_codes_remaining} backup codes restantes
              </div>
            </div>
            {!enabled && !enableData && (
              <button
                data-testid="tfa-enable-btn"
                type="button"
                onClick={onStartEnable}
                disabled={enable.isPending}
                className="rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                <Smartphone className="mr-1 inline h-3 w-3" />
                {enable.isPending ? "Generando…" : "Activar 2FA"}
              </button>
            )}
          </div>
        )}
      </WidgetCard>

      {/* Enable flow · step 2 · show QR + ask code */}
      {enableData && (
        <WidgetCard
          title="Escanea con tu app autenticadora"
          description="Compatible con Google Authenticator · Microsoft Authenticator · Authy · 1Password"
          citations={["/api/auth/2fa/enable"]}
          testId="widget-tfa-qr"
        >
          <WidgetGrid cols="lg:grid-cols-2">
            <div data-testid="tfa-qr-image-block">
              <img
                src={enableData.qr_png_base64}
                alt="Código QR 2FA"
                data-testid="tfa-qr-image"
                className="h-48 w-48 rounded border border-[var(--ag-border)] bg-white p-2"
              />
              <p className="mt-2 text-[10px] text-[var(--ag-text-dim)]">
                Si tu app no escanea, copia manualmente el secreto base32:
              </p>
              <div className="mt-1 flex items-center gap-2">
                <code className="rounded bg-[var(--ag-surface-2)] px-2 py-1 text-[11px] font-mono" data-testid="tfa-secret-text">
                  {enableData.secret}
                </code>
                <CopyButton value={enableData.secret} label="Secreto copiado" testId="tfa-copy-secret" />
              </div>
            </div>
            <form onSubmit={onVerifyEnable} className="space-y-2" data-testid="tfa-verify-form">
              <label className="block text-xs text-[var(--ag-text-dim)]">
                Introduce el código de 6 dígitos que muestra tu app:
              </label>
              <input
                data-testid="tfa-enable-verify-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                pattern="[0-9]{6}"
                value={enableCode}
                onChange={(e) => setEnableCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                className="w-32 rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-center font-mono text-lg tracking-widest"
                autoFocus
              />
              <button
                data-testid="tfa-enable-verify-btn"
                type="submit"
                disabled={enableCode.length !== 6 || enableVerify.isPending}
                className="block w-fit rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                {enableVerify.isPending ? "Verificando…" : "Verificar y activar"}
              </button>
            </form>
          </WidgetGrid>
        </WidgetCard>
      )}

      {/* Backup codes · shown ONCE */}
      {backupCodes.length > 0 && (
        <WidgetCard
          title="Códigos de respaldo · GUÁRDALOS AHORA"
          description="Estos códigos solo se mostrarán esta vez. Cada uno es de un solo uso."
          citations={["/api/auth/2fa/enable/verify", "/api/auth/2fa/backup-codes/regenerate"]}
          testId="widget-tfa-backup-codes"
        >
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5" data-testid="tfa-backup-codes-grid">
            {backupCodes.map((code, i) => (
              <code
                key={code}
                data-testid={`tfa-backup-code-${i}`}
                className="rounded border border-emerald-500/40 bg-emerald-500/5 px-2 py-1.5 text-center font-mono text-sm tracking-widest"
              >
                {code}
              </code>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <CopyButton
              value={backupCodes.join("\n")}
              label="Códigos copiados al portapapeles"
              testId="tfa-backup-copy-all"
            />
            <button
              type="button"
              data-testid="tfa-backup-dismiss"
              onClick={() => setBackupCodes([])}
              className="rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)]"
            >
              He guardado mis códigos
            </button>
          </div>
        </WidgetCard>
      )}

      {/* Management section · only when enabled */}
      {enabled && (
        <WidgetGrid cols="lg:grid-cols-2">
          <WidgetCard
            title="Regenerar códigos de respaldo"
            description="Los códigos actuales quedarán invalidados"
            citations={["/api/auth/2fa/backup-codes/regenerate"]}
            testId="widget-tfa-regenerate"
          >
            <form onSubmit={onRegenerate} className="space-y-2" data-testid="tfa-regen-form">
              <label className="block text-[11px] text-[var(--ag-text-dim)]">
                Confirma con un código TOTP de 6 dígitos:
              </label>
              <input
                data-testid="tfa-regen-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={regenCode}
                onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                className="w-32 rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-center font-mono"
              />
              <button
                data-testid="tfa-regen-btn"
                type="submit"
                disabled={regenCode.length !== 6 || regenerate.isPending}
                className="block w-fit rounded-md border border-[var(--ag-border)] px-3 py-1.5 text-xs hover:bg-[var(--ag-surface-2)] disabled:opacity-50"
              >
                <RotateCw className="mr-1 inline h-3 w-3" />
                {regenerate.isPending ? "Regenerando…" : "Regenerar 10 códigos"}
              </button>
            </form>
          </WidgetCard>

          <WidgetCard
            title="Desactivar 2FA"
            description={mandatory ? "No permitido para superadmin (rol con 2FA obligatorio)" : "Reduce la seguridad de tu cuenta"}
            citations={["/api/auth/2fa/disable"]}
            testId="widget-tfa-disable"
          >
            {mandatory ? (
              <div className="rounded border border-yellow-500/30 bg-yellow-500/5 p-2 text-xs text-yellow-600" data-testid="tfa-disable-blocked">
                <Sparkles className="mr-1 inline h-3 w-3" />
                Tu rol (superadmin) tiene 2FA obligatorio · no puedes desactivarlo.
              </div>
            ) : (
              <form onSubmit={onDisable} className="space-y-2" data-testid="tfa-disable-form">
                <label className="block text-[11px] text-[var(--ag-text-dim)]">
                  Introduce un TOTP o un código de respaldo:
                </label>
                <input
                  data-testid="tfa-disable-input"
                  type="text"
                  maxLength={12}
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  placeholder="000000 o ABCD-2345"
                  className="w-40 rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-center font-mono"
                />
                <button
                  data-testid="tfa-disable-btn"
                  type="submit"
                  disabled={disableCode.length < 6 || disable.isPending}
                  className="block w-fit rounded-md border border-red-500/40 px-3 py-1.5 text-xs text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                >
                  <ShieldOff className="mr-1 inline h-3 w-3" />
                  {disable.isPending ? "Desactivando…" : "Desactivar 2FA"}
                </button>
              </form>
            )}
          </WidgetCard>
        </WidgetGrid>
      )}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["pyotp 2.10.0", "RFC 6238", "T-14 backend · 0 cambios"]} />
      </footer>
    </section>
  );
}
